<template>
  <div v-if="selectedDayInfo" class="precept-info">
    <h4 class="text-lg font-semibold text-gray-800 mb-2 flex items-center">
      <el-icon class="mr-2" :size="20">
        <View />
      </el-icon>
      戒期详情
    </h4>

    <!-- 有斋日或戒期时显示 -->
    <div v-if="hasPreceptDays || filteredPreceptInfos.length > 0" class="space-y-3">
      <!-- 普通戒期信息 -->
      <div
        v-for="(precept, index) in filteredPreceptInfos"
        :key="index"
        class="precept-item border rounded-lg p-4"
        :class="getPreceptItemClass(precept.level)"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="flex items-center mb-2">
              <div
                class="precept-indicator mr-2"
                :class="precept.level"
              ></div>
              <span class="font-semibold">{{ getPreceptTitle(precept) }}</span>
              <el-tag
                size="small"
                class="ml-2"
                :style="getPreceptLevelStyle(precept.level)"
              >
                {{ getPreceptLevelText(precept.level) }}
              </el-tag>
              <el-tag
                type="warning"
                size="small"
                class="ml-1"
                effect="plain"
              >
                {{ precept.punishment }}
              </el-tag>
            </div>
            <div class="text-sm text-gray-600 space-y-2">
              <!-- 标签组：类型标签 + 其他标签 -->
              <div class="flex flex-wrap gap-1">
                <!-- 类型标签（第一位） -->
                <el-tag type="primary" size="small">
                  {{ getPreceptTypeText(precept.type) }}
                </el-tag>

                <!-- 其他标签 -->
                <el-tag
                  v-for="tag in precept.detail?.tags"
                  :key="tag"
                  size="small"
                  type="info"
                  effect="plain"
                  class="text-xs"
                >
                  {{ tag }}
                </el-tag>
              </div>

              <!-- 说明 -->
              <div v-if="precept.detail?.explanation" class="whitespace-pre-wrap text-gray-600">
                {{ precept.detail.explanation }}
              </div>


              <!-- 如果是斋日类型的戒期，添加斋日说明 -->
              <div v-if="precept.type === PreceptType.PRECEPT_DAY" class="bg-amber-50 rounded p-2 text-amber-700 text-sm">
                <div class="flex items-center">
                  <Star class="w-4 h-4 mr-1" />
                  <span>诸天斋日，功德倍增</span>
                </div>
              </div>

              <!-- 如果是社日，添加特殊警告 -->
              <div v-if="precept.detail?.tags?.includes('二社日')" class="bg-red-100 border border-red-300 rounded p-2 text-red-700 text-sm">
                <div class="flex items-center">
                  <WarningFilled class="w-4 h-4 mr-1" />
                  <span>社日受胎者，毛发皆白</span>
                </div>
              </div>

              <!-- 如果是二分日，添加特殊警告 -->
              <div v-if="precept.detail?.tags?.includes('二分日')" class="bg-orange-100 border border-orange-300 rounded p-2 text-orange-700 text-sm">
                <div class="flex items-center">
                  <WarningFilled class="w-4 h-4 mr-1" />
                  <span>{{ getEquinoxWarningText(precept) }}之前三后三共七日，犯之必得危疾，尤宜切戒</span>
                </div>
              </div>

              <!-- 如果是二至日，添加特殊警告 -->
              <div v-if="precept.detail?.tags?.includes('二至日')" class="bg-orange-100 border border-orange-300 rounded p-2 text-orange-700 text-sm">
                <div class="flex items-center">
                  <WarningFilled class="w-4 h-4 mr-1" />
                  <span>{{ getSolsticeWarningText(precept) }}之前三后三共七日，犯之必得急疾，尤宜切戒</span>
                </div>
                <div v-if="precept.detail?.tags?.includes('冬至') && !precept.detail?.tags?.includes('前后戒期')" class="flex items-center mt-1">
                  <WarnTriangleFilled class="w-4 h-4 mr-1" />
                  <span>冬至半夜子时犯之，主在一年内亡</span>
                </div>
              </div>

              <!-- 如果是冬至后第三戌日，添加特殊警告 -->
              <div v-if="precept.reason === '冬至后第三戌日'" class="bg-red-100 border border-red-300 rounded p-2 text-red-700 text-sm">
                <div class="flex items-center">
                  <WarnTriangleFilled class="w-4 h-4 mr-1" />
                  <span>冬至后第三戌日犯之，主在一年内亡</span>
                </div>
              </div>

              <!-- 如果是冬至后庚辛日，添加特殊警告 -->
              <div v-if="precept.reason === '冬至后庚辛日'" class="bg-red-100 border border-red-300 rounded p-2 text-red-700 text-sm">
                <div class="flex items-center">
                  <WarnTriangleFilled class="w-4 h-4 mr-1" />
                  <span>冬至后庚辛日犯之，主在一年内亡</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 如果没有戒期但有斋日，单独显示斋日提示 -->
      <div v-if="hasPreceptDays && filteredPreceptInfos.length === 0" class="text-blue-600 text-center py-4">
        诸天斋日，功德倍增
      </div>
    </div>

    <!-- 既无戒期也无斋日时的提示 -->
    <div v-if="!(hasPreceptDays || filteredPreceptInfos.length > 0)" class="precept-item border rounded-lg p-4" :class="getPreceptItemClass(PreceptLevel.SAFE)">
      <div class="flex items-start justify-between">
        <div class="flex-1">
          <div class="flex items-center mb-2">
            <div class="precept-indicator safe mr-2"></div>
            <span class="font-semibold">今日清净日</span>
            <el-tag
              size="small"
              class="ml-2"
              :style="getPreceptLevelStyle(PreceptLevel.SAFE)"
            >
              安泰
            </el-tag>
          </div>
          <div class="text-sm text-gray-600 space-y-1">
            <div class="flex items-center">
              <span class="font-medium text-gray-700 mr-2">状态：</span>
              <span class="text-green-600 font-medium">心清意净，自在安然</span>
            </div>
            <div>建议：宜精进修行，清净身心，进德修业，慎勿放逸！</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useCalendarStore } from '@/stores/calendar'
import { useSettingsStore } from '@/stores/settings'
import { CalendarUtil } from '@/utils/calendar'
import { PreceptDataManager } from '@/utils/precept'
import { PreceptType, PreceptLevel } from '@/types'
import { getPreceptLevelText } from '@/data/precept/precept-config'
import { getPreceptLevelStyle, getPreceptItemClass } from '@/constants'
import * as lunar from 'lunar-javascript'
import { View, Star, WarningFilled, WarnTriangleFilled } from '@element-plus/icons-vue'

const calendarStore = useCalendarStore()
const settingsStore = useSettingsStore()
const preceptManager = PreceptDataManager.getInstance()

const selectedDayInfo = computed(() => calendarStore.selectedDayInfo)

const filteredPreceptInfos = computed(() => {
  if (!selectedDayInfo.value) return []

  return selectedDayInfo.value.preceptInfos.filter(precept =>
    settingsStore.settings.enabledPreceptTypes.includes(precept.type)
  )
})

const hasPreceptDays = computed(() => {
  return isTenPreceptDay.value
})

const isTenPreceptDay = computed(() => {
  if (!selectedDayInfo.value) return false

  try {
    const solar = lunar.Solar.fromDate(selectedDayInfo.value.date)
    const lunarDate = solar.getLunar()
    return preceptManager.isTenPreceptDay(lunarDate.getDay())
  } catch (error) {
    console.warn('检查十斋日失败', error)
    return false
  }
})

const getPreceptTypeText = (type: string) => {
  const typeMap = {
    regular: '常规戒期',
    special: '特殊戒期',
    personal: '个人戒期',
    precept_day: '斋日'
  }
  return typeMap[type as keyof typeof typeMap] || '未知类型'
}

/**
 * 获取戒期标题
 * 对于三伏日，统一显示为"三伏日"
 */
const getPreceptTitle = (precept: any) => {
  // 检查是否为三伏日（通过标签判断）
  if (precept.detail?.tags?.includes('三伏日')) {
    return '三伏日'
  }
  return precept.reason
}

/**
 * 获取二分日警告文本中的节气名称
 */
const getEquinoxWarningText = (precept: any) => {
  const tags = precept.detail?.tags || []
  if (tags.includes('春分')) {
    return '春分'
  }
  if (tags.includes('秋分')) {
    return '秋分'
  }
  return '二分日'
}

/**
 * 获取二至日警告文本中的节气名称
 */
const getSolsticeWarningText = (precept: any) => {
  const tags = precept.detail?.tags || []
  if (tags.includes('夏至')) {
    return '夏至'
  }
  if (tags.includes('冬至')) {
    return '冬至'
  }
  return '二至日'
}
</script>

<style scoped>
.precept-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.precept-indicator.major {
  background-color: rgb(var(--color-major));
}

.precept-indicator.moderate {
  background-color: rgb(var(--color-moderate));
}

.precept-indicator.minor {
  background-color: rgb(var(--color-minor));
}

.precept-indicator.safe {
  background-color: rgb(var(--color-safe));
}

/* 戒等级标签样式 - 与指示器颜色匹配 */
.precept-level-tag.major {
  background: rgba(var(--color-major), 0.85) !important;
  border-color: rgb(var(--color-major)) !important;
  color: #ffffff !important;
}

.precept-level-tag.moderate {
  background: rgba(var(--color-moderate), 0.85) !important;
  border-color: rgb(var(--color-moderate)) !important;
  color: #ffffff !important;
}

.precept-level-tag.minor {
  background: rgba(var(--color-minor), 0.85) !important;
  border-color: rgb(var(--color-minor)) !important;
  color: #ffffff !important;
}

.precept-level-tag.safe {
  background: rgba(var(--color-safe), 0.85) !important;
  border-color: rgb(var(--color-safe)) !important;
  color: #ffffff !important;
}

/* 深度样式覆盖 */
:deep(.el-tag.precept-level-tag.major) {
  background: rgba(var(--color-major), 0.85) !important;
  border-color: rgb(var(--color-major)) !important;
  color: #ffffff !important;
}

:deep(.el-tag.precept-level-tag.moderate) {
  background: rgba(var(--color-moderate), 0.85) !important;
  border-color: rgb(var(--color-moderate)) !important;
  color: #ffffff !important;
}

:deep(.el-tag.precept-level-tag.minor) {
  background: rgba(var(--color-minor), 0.85) !important;
  border-color: rgb(var(--color-minor)) !important;
  color: #ffffff !important;
}

:deep(.el-tag.precept-level-tag.safe) {
  background: rgba(var(--color-safe), 0.85) !important;
  border-color: rgb(var(--color-safe)) !important;
  color: #ffffff !important;
}
</style>